// ** reactstrap
import { Button, Label, Popover, PopoverBody } from "reactstrap";
import { Controller, useController } from "react-hook-form";
// ** React imports
import { useEffect, useState } from "react";

import { DateRangePicker as DateRange } from "react-date-range";
import ErrorBoundaryWrapper from "@views/common/error-boundary-wrapper";
import Flex from "@components/ui-components/flex";
import ImageWrapper from "@components/ui-components/image-wrapper";
// ** Third Party
import classnames from "classnames";
import dayjs from "dayjs";
// ** Custom Components
import { defaultStaticRanges } from "./default-ranges";
// ** Toastify
import { toastify } from "@helpers/toast";
import useDateHandler from "@hooks/use-date-handler";
import useSearchParams from "@hooks/use-search-params";
import { useTranslation } from "react-i18next";
import useWindowSize from "@src/utility/hooks/use-window-size";

const clearedValue = {
    start: null,
    end  : null
};

const DateRangePicker = (props) => {

    const wrapperRef = (null);
    const buttonRef = (null);

    const [open, setOpen] = useState(false);
    const { dateFormatter } = useDateHandler();
    const { windowWidth } = useWindowSize();
    const {
        control,
        name,
        errors = {},
        className = "",
        label = "",
        required = false,
        customOnChange = () => { },
        maxDate = undefined,
        minDate = undefined,
        id,
        btnClass = "",
        rangeAllowed = null,
        isClearable = false
    } = props;

    const {
        field: { onChange, value }
    } = useController({
        name,
        control
    });
    const [range, setRange] = useState({
        startDate: value.start,
        endDate  : value.end
    });
    const { deleteParams } =
    useSearchParams();

    const handleChange = (ranges) => {
        const selectedRange = ranges.range1 || ranges.selection;
        setRange(selectedRange);
    };

    /**
   * If the user clicks outside of the ref element, then set the open range to false.
   */
    function useOutsideAlerter (ref) {
        useEffect(() => {
            function handleClickOutside (event) {
                const isOutsideClick =
                    ref?.current && !ref?.current.contains(event.target);
                const isOutsideButton =
                    buttonRef?.current && !buttonRef?.current.contains(event.target);
                // const isScrollingOutside = window.scrollY > ref.current.getBoundingClientRect().bottom;
                if (isOutsideClick && isOutsideButton) {
                    setOpen(false);
                }
            }
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("wheel", handleClickOutside);

            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
                document.removeEventListener("wheel", handleClickOutside);
            };
        }, [ref]);
    }
    useOutsideAlerter(wrapperRef);
    const { t } = useTranslation();
    const renderError = () => {
        const message = errors && errors[name]?.message;
        return (
            message && (
                <small className="text-danger" data-cy={`error-${name}`}>
                    {message}
                </small>
            )
        );
    };

    const handleSuccessApply = (action) => {
        if (range.startDate && range.endDate && action === "apply") {
            const start = range.startDate;
            const end = range.endDate;

            onChange({ start, end });
            setOpen(false);

            if (customOnChange) {
                customOnChange({ start, end });
            }
        } else {
            deleteParams("start");
            deleteParams("end");
            onChange(clearedValue);
            if (customOnChange) {
                customOnChange(clearedValue);
            }

        }
    };

    /**
   * A function that is called when the user clicks the apply button.
   */
    const handleApply = (action) => {
        if (action === "apply") {
            if (
                (rangeAllowed || rangeAllowed === 0) &&
                dayjs(range.endDate).diff(range.startDate, "day") + 1 > rangeAllowed
            ) {
                toastify({
                    title: `Maximum ${rangeAllowed === 0 ? 1 : rangeAllowed} days can be selected`,
                    type : "error"
                });
                setRange({ startDate: null, endDate: null });
            } else {
                handleSuccessApply("apply");
            }
        } else if (action === "clear") {
            handleSuccessApply("clear");
        } 
        setOpen(false);
    };

    useEffect(() => {
        if (value) {
            setRange({ startDate: value.start, endDate: value.end });
        } else {
            setRange({
                startDate: new Date(),
                endDate  : new Date()
            });
        }
    }, [value]);

    const handleToggle = () => {
        setOpen(!open);
    };

    return (
        <ErrorBoundaryWrapper
            name="picker"
            children={
                <div
                    className={classnames("range-picker", className)}
                    data-tour="range-picker">
                    {label && (
                        <Label htmlFor={name}>
                            {label}
                            {required && <span className="text-danger fw-bolder">*</span>}
                        </Label>
                    )}
                    <Controller
                        control={control}
                        name={name}
                        render={() => {
                            return (
                                <>
                                    <Button
                                        id={`input-range-${id}`}
                                        innerRef={buttonRef}
                                        data-cy={`input-range-${id}-btn`}
                                        className={classnames(
                                            "btn-sm range-picker-calendar-ctrller d-flex align-items-center border-0 p-1",
                                            {
                                                [btnClass]: btnClass
                                            }
                                        )}
                                        onClick={handleToggle}
                                        outline>
                                        <ImageWrapper
                                            src={"common/calendar.svg"}
                                            id={`input-range-${id}-img`}
                                            className={classnames("font-medium-5", {
                                                "me-lg-1": value.start
                                            })}/>
                                        {value.start || value.end ? (
                                            <div className="daterange-dates">
                                                {value.start && value.end ? `${dateFormatter({ date: value.start })} - ${dateFormatter({ date: value.end })}` : t("Select date range")}
                                            </div>
                                        ) : null}
                                        <i
                                            className={classnames(
                                                "mdi mdi-chevron-down font-medium-5 d-none d-lg-block",
                                                {
                                                    "ms-md-1": value.start
                                                }
                                            )} />
                                    </Button>
                                    <Popover
                                        target={`input-range-${id}`}
                                        trigger="focus"
                                        isOpen={open}
                                        placement="bottom">
                                        <div ref={wrapperRef}>
                                            <PopoverBody
                                                className={classnames("range-picker-popover-body", {
                                                    "start-date": value.start
                                                })}>
                                                <DateRange
                                                    direction="horizontal"
                                                    className="border range-picker"
                                                    ranges={[
                                                        {
                                                            startDate: range?.startDate || new Date(),
                                                            endDate  : range?.endDate || new Date(),
                                                            key      : "selection"
                                                        }
                                                    ]}
                                                    showSelectionPreview
                                                    moveRangeOnFirstSelection={false}
                                                    rangeColors={["#fb4a1c"]}
                                                    maxDate={maxDate}
                                                    theme={{
                                                        Calendar: { width: 200 }
                                                    }}
                                                    months={windowWidth >= 1024 ? 2 : 1}
                                                    inputRanges={[]}
                                                    minDate={minDate}
                                                    editableDateInputs
                                                    staticRanges={defaultStaticRanges().filter(
                                                        (item) => !item.omit
                                                    )}
                                                    onChange={handleChange} />
                                                <div className="picker-action-btn">
                                                    <Flex align="center" justify="end" className="w-100">
                                                        {range.startDate && range.endDate &&    <p className="pe-25 font-small-4">{`${dateFormatter({ date: range?.startDate, formatAs: "DD/MM/YYYY" })} - ${dateFormatter({ date: range?.endDate, formatAs: "DD/MM/YYYY" })}`}</p>}
                                                        <Button
                                                            className="btn-sm"
                                                            color="secondary"
                                                            onClick={() => handleToggle()}
                                                            outline>
                                                            {t("Cancel")}
                                                        </Button>
                                                        {isClearable && value.start ?    <Button
                                                            className="btn-sm"
                                                            color="danger"
                                                            onClick={() => handleApply("clear")}
                                                            outline>
                                                            {t("Clear")}
                                                        </Button> : null}
                                                        <Button
                                                            data-cy={"datepicker-apply"}
                                                            color="primary"
                                                            className="btn-sm"
                                                            onClick={() => handleApply("apply")}>
                                                            {t("Apply")}
                                                        </Button>
                                                    </Flex>
                                                </div>
                                            </PopoverBody>
                                        </div>
                                    </Popover>
                                </>
                            );
                        }} />            {renderError()}
                </div>
            }/>
     
    );
};

export default DateRangePicker;